import React, { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { TimePeriod } from '../../types/api';
import { useSubscriptionByOrderType } from '../../hooks/useSubscriptionQueries';
import { useTheme } from '../../contexts/ThemeContext';
import { ChartControls, ChartGuideModal, HorizontalTableShell } from '../ui';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { getChartColors, getChartBackgroundColor, getChartJsOptions, formatTooltipLabel, mergeChartOptions } from '../../utils/themeHelpers';
import { formatSubscriptionPercentage, toSubscriptionPercentage } from '../../utils/formatters';
import {
  computeSubscriptionMonthTotal,
  groupSubscriptionMonthItems,
  toSubscriptionTableCell,
} from '../../utils/subscriptionTableHelpers';
import { TYPOGRAPHY_CONFIG } from '../../config/typography.config';

interface OrderTypeSubscriptionProps {
  branchId?: number;
  timeRange?: TimePeriod;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
}

const orderTypeOrder = ['سالن', 'بسته بندی', 'دلیوری', 'اسنپ', 'سایت اختصاصی'];

const getPercentageRange = (datasets: any[]) => {
  const values = datasets.flatMap(dataset => dataset.data.filter((value: any) => value !== null && value !== undefined));
  if (!values.length) return { min: 0, max: 105 };

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue;

  if (range < 20 && minValue > 20) {
    const padding = Math.max(5, range * 0.2);
    return { min: Math.max(0, Math.floor(minValue - padding)), max: Math.min(100, Math.ceil(maxValue + padding)) };
  }

  return { min: 0, max: 105 };
};

const OrderTypeSubscription: React.FC<OrderTypeSubscriptionProps> = ({
  branchId = 0,
  timeRange,
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { isModalOpen, currentGuide, openGuide, closeGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data: orderTypeData, isLoading, error } = useSubscriptionByOrderType(branchId, timeRange);
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');

  const componentId = 'order-type-subscription';
  const componentTitle = 'اشتراک‌گیری بر اساس نوع سفارش';
  const chartColors = getChartColors(currentTheme);
  const orderTypeColorMapping: Record<string, number> = { 'سالن': 0, 'بسته بندی': 1, 'دلیوری': 2, 'اسنپ': 6, 'سایت اختصاصی': 7 };

  useEffect(() => {
    if (error && onError) onError(error as Error);
  }, [error, onError]);

  const orderedOrderTypes = (() => {
    if (!orderTypeData) return [];
    const uniqueOrderTypes = Array.from(new Set(orderTypeData.map(item => item.order_type)));
    return [
      ...orderTypeOrder.filter(type => uniqueOrderTypes.includes(type)),
      ...uniqueOrderTypes.filter(type => !orderTypeOrder.includes(type))
    ];
  })();

  const chartData = (() => {
    if (!orderTypeData || !orderTypeData.length) return { labels: [], datasets: [] };

    const months = Array.from(new Set(orderTypeData.map(item => item.month))).sort((a, b) => {
      const aItem = orderTypeData.find(item => item.month === a);
      const bItem = orderTypeData.find(item => item.month === b);
      return (aItem?.yearMonth || 0) - (bItem?.yearMonth || 0);
    });

    const datasets = orderedOrderTypes.map(orderType => {
      const colorIndex = orderTypeColorMapping[orderType] ?? 0;
      const color = chartColors[colorIndex % chartColors.length];
      const backgroundColor = color.startsWith('#')
        ? `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)}, 0.2)`
        : getChartBackgroundColor(colorIndex, currentTheme, 0.2);

      return {
        label: orderType,
        data: months.map(month => toSubscriptionPercentage(
          orderTypeData.find(item => item.month === month && item.order_type === orderType)?.subscription_percentage
        )),
        borderColor: color,
        backgroundColor,
        borderWidth: 2,
        pointBackgroundColor: color,
        pointBorderColor: currentTheme === 'light' ? '#FFFFFF' : '#27272A',
        pointRadius: 6,
        pointHoverRadius: 10,
        tension: 0.3,
        fill: false
      };
    });

    return { labels: months, datasets };
  })();

  const tableData = (() => {
    if (!orderTypeData || !orderTypeData.length) return { headers: [], rows: [] };

    const months = Array.from(new Set(orderTypeData.map(item => item.month))).sort((a, b) => {
      const aItem = orderTypeData.find(item => item.month === a);
      const bItem = orderTypeData.find(item => item.month === b);
      return (bItem?.yearMonth || 0) - (aItem?.yearMonth || 0);
    });

    const rows = months.map(month => {
      const monthItems = orderTypeData.filter(item => item.month === month);
      const groupedByOrderType = groupSubscriptionMonthItems(monthItems, item => item.order_type);
      const row: Record<string, any> = { month, yearMonth: monthItems[0]?.yearMonth || 0 };

      orderedOrderTypes.forEach(orderType => {
        row[orderType] = toSubscriptionTableCell(groupedByOrderType.get(orderType));
      });

      row['مجموع'] = computeSubscriptionMonthTotal(monthItems);
      return row;
    });

    return { headers: ['ماه', ...orderedOrderTypes, 'مجموع'], rows };
  })();

  const yAxisRange = getPercentageRange(chartData.datasets);
  const chartOptions = mergeChartOptions(getChartJsOptions(currentTheme), {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: !compact },
      tooltip: {
        ...getChartJsOptions(currentTheme).plugins.tooltip,
        callbacks: {
          label: (context: any) => context.parsed.y === null
            ? ''
            : formatTooltipLabel(context.dataset.label || '', formatSubscriptionPercentage(context.parsed.y))
        }
      }
    },
    scales: {
      ...getChartJsOptions(currentTheme).scales,
      y: {
        ...getChartJsOptions(currentTheme).scales?.y,
        min: yAxisRange.min,
        max: yAxisRange.max,
        title: {
          display: !compact,
          text: 'درصد اشتراک‌گیری',
          color: currentTheme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          font: {
            family: 'var(--font-primary)',
            size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
            weight: TYPOGRAPHY_CONFIG.components.chart.axisLabel.weight,
          }
        },
        ticks: {
          ...getChartJsOptions(currentTheme).scales?.y?.ticks,
          callback: (value: any) => `${value}%`,
          stepSize: yAxisRange.max - yAxisRange.min < 30 ? 5 : 10,
        }
      },
      x: {
        ...getChartJsOptions(currentTheme).scales?.x,
        title: {
          display: !compact,
          text: 'ماه',
          color: currentTheme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          font: {
            family: 'var(--font-primary)',
            size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
            weight: TYPOGRAPHY_CONFIG.components.chart.axisLabel.weight,
          }
        }
      }
    }
  });

  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="animate-pulse">
          <div className="h-6 bg-bg-secondary rounded w-1/3 mb-4"></div>
          <div className="h-80 bg-bg-secondary rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="card-base card-padding animate-enter animate-delay-3 text-center text-status-error">خطا در بارگذاری داده های اشتراک‌گیری نوع سفارش</div>;
  }

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <div className="mb-6">
        {!compact && (
          <ChartControls
            componentId={componentId}
            componentTitle={componentTitle}
            componentType="mixed"
            dashboardSource="subscription"
            onInfo={() => openGuide(CHART_GUIDES.orderTypeSubscription)}
            onFavorite={() => handleFavoriteToggle({
              componentId,
              componentTitle,
              dashboardSource: 'subscription',
              componentType: 'mixed',
              description: 'نمودار و جدول اشتراک‌گیری بر اساس نوع سفارش',
              props: { branchId, timeRange, onError, showLoading, compact },
              gridLayout: { containerClass: 'card-base card-padding' }
            })}
            isFavorited={isFavorited(componentId)}
            className="mb-4"
            chartRef={chartRef}
          />
        )}
        <h3 className="title-card">{viewMode === 'chart' ? componentTitle : 'جدول اشتراک‌گیری به تفکیک نوع سفارش'}</h3>
        {!compact && (
          <div className="flex gap-2">
            <button className={`px-4 py-2 rounded-lg font-medium transition-all ${viewMode === 'chart' ? 'bg-primary-600 text-text-inverse shadow-md' : 'bg-bg-secondary text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'}`} onClick={() => setViewMode('chart')}>
              نمودار اشتراک‌گیری
            </button>
            <button className={`px-4 py-2 rounded-lg font-medium transition-all ${viewMode === 'table' ? 'bg-primary-600 text-text-inverse shadow-md' : 'bg-bg-secondary text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'}`} onClick={() => setViewMode('table')}>
              جدول اشتراک‌گیری
            </button>
          </div>
        )}
      </div>

      {viewMode === 'chart' ? (
        orderTypeData && orderTypeData.length ? (
          <div className={compact ? 'h-60' : 'h-80'}>
            <Line data={chartData} options={chartOptions} height={compact ? 240 : 300} />
          </div>
        ) : (
          <div className="h-80 flex items-center justify-center text-text-secondary">داده ای برای نمایش وجود ندارد</div>
        )
      ) : (
        <HorizontalTableShell minWidth={Math.max(720, tableData.headers.length * 110)}>
            <table className="w-full border-collapse" style={{ direction: 'rtl' }}>
              <thead>
                <tr className="border-b-2 !bg-gray-100 dark:!bg-gray-800" style={{ borderColor: 'var(--border-secondary)' }}>
                  {tableData.headers.map((header, index) => {
                    const isTotal = header === 'مجموع';
                    return (
                      <th
                        key={index}
                        className={`px-3 py-4 text-center text-sm font-bold border-l last:border-l-0 ${isTotal ? '!bg-primary-600 text-text-inverse' : 'text-text-primary'}`}
                        style={{ borderColor: 'var(--border-primary)' }}
                      >
                        {header}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {tableData.rows.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={`transition-all border-b ${rowIndex % 2 === 0 ? '!bg-white dark:!bg-gray-900' : '!bg-gray-50 dark:!bg-gray-800'} hover:!bg-blue-50 dark:hover:!bg-gray-700`}
                    style={{ borderColor: 'var(--border-primary)' }}
                  >
                    {tableData.headers.map((header, cellIndex) => {
                      if (header === 'ماه') {
                        return (
                          <td
                            key={cellIndex}
                            className="px-3 py-4 whitespace-nowrap text-center text-sm font-bold border-l last:border-l-0 text-text-primary bg-bg-tertiary"
                            style={{ borderColor: 'var(--border-primary)' }}
                          >
                            {row.month}
                          </td>
                        );
                      }
                      const cell = row[header];
                      const percentage = cell?.percentage;
                      const factureCount = cell?.factureCount;
                      const isTotal = header === 'مجموع';
                      return (
                        <td
                          key={cellIndex}
                          className={`px-3 py-4 whitespace-nowrap text-center text-sm border-l last:border-l-0 ${isTotal ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-bold' : 'text-text-secondary font-medium'}`}
                          style={{ borderColor: 'var(--border-primary)' }}
                        >
                          {percentage === null || percentage === undefined ? (
                            <span className="text-text-tertiary">-</span>
                          ) : (
                            <div className="flex flex-col items-center gap-1">
                              <span className="font-semibold">{formatSubscriptionPercentage(percentage)}</span>
                              <span className={`text-xs font-normal ${isTotal ? 'text-primary-600 dark:text-primary-300' : 'text-text-tertiary'}`}>
                                {`${Number(factureCount ?? 0).toLocaleString('fa-IR')} سفارش`}
                              </span>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
        </HorizontalTableShell>
      )}

      {currentGuide && !compact && (
        <ChartGuideModal
          isOpen={isModalOpen}
          onClose={closeGuide}
          title={currentGuide.title}
        >
          <div className="space-y-8">
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">توضیحات</h3>
              <p className="text-text-secondary">{currentGuide.description}</p>
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نحوه تفسیر</h3>
              <p className="text-text-secondary">{currentGuide.interpretation}</p>
            </div>
            {currentGuide.tips.length > 0 && (
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نکات کلیدی</h3>
                <ul className="list-disc list-inside space-y-2">
                  {currentGuide.tips.map((tip, index) => (
                    <li key={index} className="text-text-secondary">{tip}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </ChartGuideModal>
      )}
    </div>
  );
};

export default OrderTypeSubscription;
